import React, { useState } from "react";
import morningPeakLoadImg from "../../../icons/morningPeakLoad.png";
import solarPeakLoadImg from "../../../icons/SolarPeakLoad.png";
import eveningPeakLoadImg from "../../../icons/EveningPeakLoad.png";
import { scaleUnit } from "./unitScale";

type WeatherStats = {
  temp?: number | null;
  humid?: number | null;
  solar?: number | null;
};

type PeakCardProps = {
  title: string;
  peakBlock: number | null;
  peakDate?: string | null;
  peakValue: number | null;
  peakPrevDay?: number | null;
  unit: string;
  weather?: WeatherStats | null;
  weatherPrevDay?: WeatherStats | null;
  showPrevDay?: boolean;
  autoScaleUnit?: boolean;
};

function formatDate(iso: string) {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

// slot_48 (1-48, 30-min blocks) → block start time in 24h HH:MM.
// Block 1 = 00:00, 2 = 00:30 ... 20 = 09:30, 48 = 23:30.
function slotToTime(slot: number | null | undefined): string | null {
  if (slot == null || slot < 1) return null;
  const mins = (slot - 1) * 30;
  const hh = String(Math.floor(mins / 60)).padStart(2, "0");
  const mm = String(mins % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}

function fmt(v: number | null | undefined): string {
  if (v == null) return "—";
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

const WeatherIcon = ({ type }: { type: "temp" | "humid" | "solar" }) => {
  if (type === "temp")
    return (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z" />
      </svg>
    );
  if (type === "humid")
    return (
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
      </svg>
    );
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="4" />
      <line x1="12" y1="20" x2="12" y2="22" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="2" y1="12" x2="4" y2="12" />
      <line x1="20" y1="12" x2="22" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
};

const WEATHER_ITEMS: {
  label: string;
  iconType: "temp" | "humid" | "solar";
  key: keyof WeatherStats;
  unit: string;
}[] = [
  { label: "Temperature", iconType: "temp", key: "temp", unit: "°C" },
  { label: "Humidity", iconType: "humid", key: "humid", unit: "%" },
  { label: "Solar Irrad.", iconType: "solar", key: "solar", unit: "W/m²" },
];

const PEAK_ICON_SRCS: Record<string, string> = {
  Morning: morningPeakLoadImg,
  Solar: solarPeakLoadImg,
  Evening: eveningPeakLoadImg,
};

function getPeakIcon(title: string): React.ReactNode {
  const key = Object.keys(PEAK_ICON_SRCS).find((k) => title.startsWith(k));
  if (key) {
    return (
      <img
        src={PEAK_ICON_SRCS[key]}
        alt={key}
        width={50}
        height={50}
        style={{ objectFit: "contain" }}
      />
    );
  }
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

export function PeakCard({
  title,
  peakBlock,
  peakDate,
  peakValue,
  peakPrevDay,
  unit,
  weather,
  weatherPrevDay,
  showPrevDay = false,
  autoScaleUnit = false,
}: PeakCardProps) {
  const displayTitle = title.endsWith("Load") ? title : `${title} Load`;

  const peakScaled = autoScaleUnit
    ? scaleUnit(peakValue, unit)
    : { value: peakValue, unit };
  const peakPrevScaled = autoScaleUnit
    ? scaleUnit(peakPrevDay, unit)
    : { value: peakPrevDay, unit };

  const isExpanded = showPrevDay;
  const cardHeight = isExpanded ? "122px" : "110px";

  return (
    <div
      className="bg-white rounded-[20px] border border-[#E8EDF2] flex flex-col shadow-sm hover:shadow-xl transition-all duration-300 ease-in-out overflow-hidden"
      style={{
        height: cardHeight,
      }}
    >
      <div className="pt-3 pb-2.5 px-4 flex flex-col gap-2 h-full">
        {/* Header row */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm text-white">
              {getPeakIcon(title)}
            </div>
            <span className="text-[13px] font-semibold text-[#6F6D7C] tracking-tight whitespace-nowrap">
              {displayTitle}
            </span>
          </div>

          {/* Date + Slot badges */}
          {peakDate && (
            <div className="flex items-center gap-1.5 shrink-0">
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-md border border-[#E6E9EF] bg-[#f6f7fa] text-[#6E7783] whitespace-nowrap">
                {formatDate(peakDate)}
              </span>
              {peakBlock != null && slotToTime(peakBlock) && (
                <span className="text-[10px] font-medium text-white px-1.5 py-0.5 rounded-[6px] bg-[#A9A8B1] whitespace-nowrap">
                  {slotToTime(peakBlock)}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-[#F0F4F8]" />

        {/* Bottom values row */}
        <div className="flex items-center justify-between">
          {/* Value on the left */}
          <div className="min-w-0 flex-shrink-0">
            <p className="text-[15px] font-medium text-[#8C8A96] leading-tight tracking-tight">
              {peakValue == null ? (
                <span className="text-[#A3AED0]">—</span>
              ) : (
                <>
                  {(peakScaled.value as number).toLocaleString("en-IN", {
                    maximumFractionDigits: 2,
                  })}{" "}
                  <span className="text-[11px] font-semibold text-[#8C8A96] ml-1 uppercase">
                    {peakScaled.unit}
                  </span>
                </>
              )}
            </p>
            {showPrevDay &&
              peakPrevDay != null &&
              peakValue != null &&
              (() => {
                const diff = peakPrevDay - peakValue;
                const color =
                  diff > 0 ? "#22c55e" : diff < 0 ? "#ef4444" : "#8C8A96";
                const arrow = diff > 0 ? "▲" : diff < 0 ? "▼" : "";
                return (
                  <p
                    className="text-[10px] font-semibold leading-tight flex items-center gap-0.5 mt-1"
                    style={{ color }}
                  >
                    {arrow && <span className="text-[8px]">{arrow}</span>}
                    {(peakPrevScaled.value as number).toLocaleString("en-IN", {
                      maximumFractionDigits: 2,
                    })}{" "}
                    <span className="text-[9px] font-normal text-[#8C8A96]">
                      prev day
                    </span>
                  </p>
                );
              })()}
          </div>

          {/* Weather stats on the right */}
          <div className="flex items-center gap-4 flex-wrap">
            {WEATHER_ITEMS.map((item) => {
              const val = weather?.[item.key];
              const prev = weatherPrevDay?.[item.key];
              return (
                <div key={item.key} className="flex items-center gap-1 min-w-0">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 border border-[#E8EDF2] bg-white text-[#6B7280] shadow-sm">
                    <WeatherIcon type={item.iconType} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold text-[#6F6D7C] leading-tight whitespace-nowrap">
                      {fmt(val)}{" "}
                      <span className="text-[9px] font-semibold text-[#6B7280]">
                        {item.unit}
                      </span>
                    </p>
                    <p className="text-[9px] font-bold text-[#9098A4]  uppercase leading-tight mt-0.5">
                      {item.iconType === "temp"
                        ? "TEMP"
                        : item.iconType === "humid"
                          ? "HUMID"
                          : "SOLAR"}
                    </p>
                    {showPrevDay &&
                      val != null &&
                      prev != null &&
                      (() => {
                        const diff = prev - val;
                        const color =
                          diff > 0
                            ? "#22c55e"
                            : diff < 0
                              ? "#ef4444"
                              : "#8C8A96";
                        const arrow = diff > 0 ? "▲" : diff < 0 ? "▼" : "";
                        return (
                          <p
                            className="text-[9px] font-semibold leading-tight flex items-center gap-0.5 mt-0.5"
                            style={{ color }}
                          >
                            {arrow && (
                              <span className="text-[8px]">{arrow}</span>
                            )}
                            {fmt(prev)}{" "}
                            <span className="text-[8px] font-normal text-[#8C8A96]">
                              prev
                            </span>
                          </p>
                        );
                      })()}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
